import crypto from 'crypto';
import { getENV } from '@libs/common-next';

/**
 * Signs a given URL using HMAC-SHA1 and a provided secret key.
 * @param urlToSign - The URL to sign.
 * @param secretKey - The base64-encoded secret key.
 * @returns The signed URL signature.
 */
function signURL(urlToSign: string, secretKey: string): string {
    const decodedKey = Buffer.from(secretKey, 'base64'); // Decode the secret key
    const hmac = crypto.createHmac('sha1', decodedKey); // Create HMAC instance
    hmac.update(urlToSign);
    const signature = hmac.digest('base64'); // Generate base64 signature

    // Convert signature to 'web-safe'
    let webSafeSignature = signature.replace(/\+/g, '-').replace(/\//g, '_');

    // some sonar rule to avoid making it web-safe with regexp
    const paddingIndex = webSafeSignature.indexOf('=');
    if (paddingIndex !== -1) {
        webSafeSignature = webSafeSignature.substring(0, paddingIndex);
    }

    return webSafeSignature;
}

// get keys from .env file
function getStaticMapsConfig() {
    return {
        apiKey: getENV('NEXT_PUBLIC_GOOGLE_MAP_API_KEY') ?? '',
        secretKey: getENV('GOOGLE_STATIC_MAPS_SECRET_KEY') ?? '',
    };
}

/**
 * Generates a Google Static Maps URL for a given latitude, longitude, and optional parameters like zoom, map size, and marker color.
 * @param params - An object containing the parameters for generating the URL:
 *    - `latitude` (number) - The latitude of the location.
 *    - `longitude` (number) - The longitude of the location.
 *    - `zoomSize` (number, optional) - The zoom level for the map (default is 12).
 *    - `mapSize` (string, optional) - The size of the map image in 'WxH' format (default is '392x200').
 *    - `markerColor` (string, optional) - The color of the marker (default is 'red').
 * @returns A signed Google Static Maps URL.
 */
export function createGoogleStaticMapsURL({
    latitude,
    longitude,
    zoomSize,
    mapSize,
    markerColor,
}: Record<string, number | string | undefined>): string {
    const { apiKey, secretKey } = getStaticMapsConfig();

    if (!apiKey || !secretKey) {
        throw new Error(
            'Google Maps API key or secret key is not configured in .env file.'
        );
    }

    const baseGoogleURL = `/maps/api/staticmap?center=${latitude},${longitude}&zoom=${
        zoomSize ?? 12
    }&size=${mapSize ?? '392x200'}&markers=color:${
        markerColor ?? 'red'
    }|${latitude},${longitude}&key=${apiKey}`;

    const signature = signURL(baseGoogleURL, secretKey);

    return `https://maps.googleapis.com${baseGoogleURL}&signature=${signature}`;
}
